import { useEffect, useMemo, useState } from "react";
import { useStudents } from "@/hooks/useStudents";
import { useDongClaims } from "@/hooks/useDongClaims";
import { useAuth } from "@/store/auth";
import { fmtYearMonth } from "@/lib/format";
import { addDoc, collection, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { withOrg } from "@/lib/org";
import { db } from "@/lib/firebase";
import { Student } from "@/types";
import { claimKey } from "@/lib/claims";
import { plainError } from "@/lib/plainError";
import { normalizeSchool } from "@/lib/school";
import clsx from "clsx";
import MapView, { Marker, Popup } from "@/components/MapView";
import { dongBadgeIcon, dotIcon, schoolBadgeIcon, STATUS_COLORS } from "@/lib/map";

export default function ClaimDongs() {
  const { user } = useAuth();
  const ym = fmtYearMonth();
  const prevYm = fmtYearMonth(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1));
  const { students, loading: studentsLoading } = useStudents({ status: "active" });
  // 권역 시군구 목록 — 하드코딩 대신 데이터에서 도출(전국화)
  const guList = useMemo(
    () => Array.from(new Set(students.map((s) => s.gu).filter(Boolean))).sort(),
    [students],
  );
  const { claims, loading } = useDongClaims(ym);
  const { claims: prevClaims } = useDongClaims(prevYm); // 「지난달 담당 그대로」용(예행연습 02-18)
  const [saveMsg, setSaveMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const [gu, setGu] = useState<string>("");
  const [view, setView] = useState<"chip" | "map">("map");
  const [groupBy, setGroupBy] = useState<"dong" | "school">("dong");
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [initd, setInitd] = useState(false);
  const [saving, setSaving] = useState(false);

  const counts = useMemo(() => {
    const m: Record<string, { gu: string; dong: string; count: number; lat?: number; lng?: number; students: Student[] }> = {};
    for (const s of students) {
      if (s.courierFlag) continue;
      const key = `${s.gu}|${s.dong}`;
      if (!m[key]) m[key] = { gu: s.gu, dong: s.dong, count: 0, students: [] };
      m[key].count++;
      m[key].students.push(s);
    }
    // 동 이름별 centroid
    for (const k of Object.keys(m)) {
      const withCoord = m[k].students.filter((s) => s.lat && s.lng);
      if (withCoord.length) {
        m[k].lat = withCoord.reduce((sum, s) => sum + (s.lat || 0), 0) / withCoord.length;
        m[k].lng = withCoord.reduce((sum, s) => sum + (s.lng || 0), 0) / withCoord.length;
      }
    }
    return Object.values(m)
      .filter((x) => x.gu && x.dong)
      .sort((a, b) => (a.gu === b.gu ? b.count - a.count : a.gu.localeCompare(b.gu)));
  }, [students]);

  const filtered = gu ? counts.filter((c) => c.gu === gu) : counts;

  const schoolCounts = useMemo(() => {
    const m: Record<string, {
      school: string;
      count: number;
      lat?: number;
      lng?: number;
      students: Student[];
      dongs: { gu: string; dong: string; count: number }[];
    }> = {};
    for (const s of students) {
      if (s.courierFlag) continue;
      if (!s.school) continue;
      // 표기 차이("개림중"↔"개림중학교")를 정규화 키로 묶어 한 학교로 합친다
      const key = normalizeSchool(s.school);
      if (!m[key]) m[key] = { school: key, count: 0, students: [], dongs: [] };
      m[key].count++;
      m[key].students.push(s);
    }
    for (const k of Object.keys(m)) {
      const withCoord = m[k].students.filter((s) => s.lat && s.lng);
      if (withCoord.length) {
        m[k].lat = withCoord.reduce((sum, s) => sum + (s.lat || 0), 0) / withCoord.length;
        m[k].lng = withCoord.reduce((sum, s) => sum + (s.lng || 0), 0) / withCoord.length;
      }
      const dongMap: Record<string, { gu: string; dong: string; count: number }> = {};
      for (const s of m[k].students) {
        if (!s.gu || !s.dong) continue;
        const dk = `${s.gu}|${s.dong}`;
        if (!dongMap[dk]) dongMap[dk] = { gu: s.gu, dong: s.dong, count: 0 };
        dongMap[dk].count++;
      }
      m[k].dongs = Object.values(dongMap).sort((a, b) => b.count - a.count);
    }
    return Object.values(m).sort((a, b) => b.count - a.count);
  }, [students]);

  const schoolFiltered = gu
    ? schoolCounts.filter((sc) => sc.dongs.some((d) => d.gu === gu))
    : schoolCounts;

  // 현재 Firestore에 저장된 내 담당 (확정 시 diff·삭제용). 키: 동="gu|dong", 학교="S::학교명"
  const savedMap = new Map<string, string>();
  const claimedByOthers: Record<string, string[]> = {};
  for (const c of claims) {
    const key = claimKey(c);
    if (c.driverUid === user?.uid) savedMap.set(key, c.id);
    else {
      claimedByOthers[key] = claimedByOthers[key] || [];
      claimedByOthers[key].push(c.driverName || c.driverUid.slice(0, 6));
    }
  }

  // 첫 로드 시 드래프트를 저장된 담당으로 초기화 (이후 토글한 임시 선택을 보관)
  useEffect(() => {
    if (loading || initd) return;
    const mine = new Set<string>();
    claims.forEach((c) => {
      if (c.driverUid === user?.uid) mine.add(claimKey(c));
    });
    setDraft(mine);
    setInitd(true);
  }, [loading, initd, claims, user]);

  // 저장본 대비 드래프트 변경분
  const dirty = savedMap.size !== draft.size || [...draft].some((k) => !savedMap.has(k));
  const addCount = [...draft].filter((k) => !savedMap.has(k)).length;
  const delCount = [...savedMap.keys()].filter((k) => !draft.has(k)).length;

  // 학교 담당(scope=school) 상태 — 학교 키 하나로 그 학교 학생만 담당.
  const schoolState = (sc: { school: string }): "mine" | "other" | "none" => {
    const k = `S::${sc.school}`;
    if (draft.has(k)) return "mine";
    if (claimedByOthers[k]?.length) return "other";
    return "none";
  };

  // 동 단위 임시 선택 토글 (확정 전까지 로컬 드래프트에만 반영)
  const toggle = (item: { gu: string; dong: string; count?: number }) => {
    const key = `${item.gu}|${item.dong}`;
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // 학교 단위 담당 — 그 "학교 학생만" 담당(동 무관, 타교 안 딸려옴). 학교 키 하나를 토글.
  const toggleSchool = (sc: { school: string }) => {
    setDraft((prev) => {
      const next = new Set(prev);
      const k = `S::${sc.school}`;
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  // 지난달 내 담당을 그대로 이번 달 드래프트로(남이 이미 맡은 곳은 뺀다). 확정을 눌러야 저장된다
  const myPrevKeys = prevClaims.filter((c) => c.driverUid === user?.uid).map(claimKey);
  const copyPrev = () => {
    const next = new Set(myPrevKeys.filter((k) => !claimedByOthers[k]?.length));
    setDraft(next);
    setSaveMsg({ kind: "ok", text: `지난달 담당 ${next.size}곳을 가져왔습니다. 아래 「확정」을 눌러야 저장됩니다.` });
  };

  // 저장 안 한 채 화면을 닫으면 잃는다 — 브라우저가 한 번 묻게 한다
  useEffect(() => {
    if (!dirty) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  // 드래프트를 Firestore에 반영 (확정) — 추가분 addDoc, 제거분 deleteDoc. 실패하면 이유를 화면에 남긴다
  const commit = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      for (const key of draft) {
        if (savedMap.has(key)) continue;
        if (key.startsWith("S::")) {
          const school = key.slice(3);
          const sc = schoolCounts.find((x) => x.school === school);
          await addDoc(collection(db, "dongClaims"), withOrg({
            yearMonth: ym,
            scope: "school",
            school,
            gu: "",
            dong: "",
            driverUid: user!.uid,
            driverName: user!.displayName || user!.email || "",
            studentCount: sc?.count ?? 0,
            claimedAt: serverTimestamp(),
          }));
        } else {
          const [g, d] = key.split("|");
          const c = counts.find((x) => x.gu === g && x.dong === d);
          await addDoc(collection(db, "dongClaims"), withOrg({
            yearMonth: ym,
            scope: "dong",
            gu: g,
            dong: d,
            driverUid: user!.uid,
            driverName: user!.displayName || user!.email || "",
            studentCount: c?.count ?? 0,
            claimedAt: serverTimestamp(),
          }));
        }
      }
      for (const [key, id] of savedMap) {
        if (!draft.has(key)) await deleteDoc(doc(db, "dongClaims", id));
      }
      setSaveMsg({ kind: "ok", text: "이번 달 담당을 저장했습니다." });
    } catch (e) {
      setSaveMsg({ kind: "err", text: `저장하지 못했습니다 — ${plainError(e)} 「확정」을 다시 눌러 주세요.` });
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setDraft(new Set(savedMap.keys()));

  const draftDongCount = [...draft].filter((k) => !k.startsWith("S::")).length;
  const draftSchoolCount = [...draft].filter((k) => k.startsWith("S::")).length;
  const draftHouseholds = [...draft].reduce((sum, k) => {
    if (k.startsWith("S::")) {
      const sc = schoolCounts.find((x) => x.school === k.slice(3));
      return sum + (sc?.count || 0);
    }
    const [g, d] = k.split("|");
    const c = counts.find((x) => x.gu === g && x.dong === d);
    return sum + (c?.count || 0);
  }, 0);

  return (
    <div className="space-y-4">
      {dirty && (
        <div className="sticky top-0 z-20 rounded-xl bg-amber-100 border border-amber-300 p-3 text-sm text-amber-900 flex items-center justify-between gap-2 flex-wrap">
          <span>⚠️ 아직 저장되지 않았습니다. 「확정」을 눌러야 이번 달 담당으로 저장됩니다.</span>
          <button onClick={commit} disabled={saving} className="btn-primary min-h-[44px]">{saving ? "저장 중…" : "확정(저장)"}</button>
        </div>
      )}
      {saveMsg && <div className={clsx("card p-3 text-sm", saveMsg.kind === "ok" ? "text-emerald-700" : "text-rose-700")} role="status">{saveMsg.text}</div>}
      {initd && savedMap.size === 0 && draft.size === 0 && myPrevKeys.length > 0 && (
        <div className="card p-3 text-sm flex items-center justify-between gap-2 flex-wrap">
          <span>지난달({prevYm})에 맡았던 곳 {myPrevKeys.length}곳이 있습니다. 이번 달도 같으면 그대로 가져오세요.</span>
          <button className="btn-secondary min-h-[44px]" onClick={copyPrev}>지난달 담당 그대로 가져오기</button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">📍 {ym} 담당 동네 고르기</h1>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">선택 동 {draftDongCount} · 학교 {draftSchoolCount} · 약 {draftHouseholds}가구</span>
          {dirty && (
            <>
              <button
                onClick={commit}
                disabled={saving}
                className="px-3 py-2 min-h-[44px] rounded-lg bg-milk-600 text-white text-sm font-semibold hover:bg-milk-700 disabled:opacity-50"
              >
                {saving ? "확정 중…" : `확정${addCount ? ` +${addCount}` : ""}${delCount ? ` -${delCount}` : ""}`}
              </button>
              <button onClick={revert} disabled={saving} className="px-2 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100">
                되돌리기
              </button>
            </>
          )}
          <div className="inline-flex rounded-lg border bg-white text-xs overflow-hidden">
            <button
              onClick={() => setGroupBy("dong")}
              className={clsx("px-3 py-1.5", groupBy === "dong" ? "bg-milk-500 text-white" : "text-slate-600")}
            >동별</button>
            <button
              onClick={() => setGroupBy("school")}
              className={clsx("px-3 py-1.5", groupBy === "school" ? "bg-milk-500 text-white" : "text-slate-600")}
            >학교별</button>
          </div>
          <div className="inline-flex rounded-lg border bg-white text-xs overflow-hidden">
            <button
              onClick={() => setView("map")}
              className={clsx("px-3 py-1.5", view === "map" ? "bg-milk-500 text-white" : "text-slate-600")}
            >지도</button>
            <button
              onClick={() => setView("chip")}
              className={clsx("px-3 py-1.5", view === "chip" ? "bg-milk-500 text-white" : "text-slate-600")}
            >목록</button>
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-500">
        <strong>동별</strong>은 그 동네 집을 전부, <strong>학교별</strong>은 그 학교 학생 집만 맡습니다. 선택 후 <strong>확정</strong>을 눌러야 저장됩니다.
      </p>

      <div className="flex gap-2 flex-wrap items-center">
        <button
          onClick={() => setGu("")}
          className={clsx("chip cursor-pointer", !gu ? "bg-milk-500 text-white" : "bg-slate-100 text-slate-700")}
        >
          전체
        </button>
        {guList.map((g) => (
          <button
            key={g}
            onClick={() => setGu(g)}
            className={clsx("chip cursor-pointer", gu === g ? "bg-milk-500 text-white" : "bg-slate-100 text-slate-700")}
          >
            {g}
          </button>
        ))}
      </div>

      {view === "chip" && groupBy === "dong" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {filtered.map((item) => {
            const key = `${item.gu}|${item.dong}`;
            const mine = draft.has(key);
            const others = claimedByOthers[key] || [];
            return (
              <button
                key={key}
                onClick={() => toggle(item)}
                className={clsx(
                  "card p-3 text-left transition relative",
                  mine ? "ring-2 ring-milk-500 bg-milk-50" : others.length ? "bg-slate-50" : "hover:bg-slate-50",
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500">{item.gu}</div>
                    <div className="font-bold">{item.dong}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-extrabold text-milk-700">{item.count}</div>
                    <div className="text-[10px] text-slate-400">가구</div>
                  </div>
                </div>
                {mine && <div className="mt-1 chip bg-milk-500 text-white text-[10px]">내가 담당 중</div>}
                {!mine && others.length > 0 && (
                  <div className="mt-1 text-[10px] text-slate-500">
                    다른 기사: {others.join(", ")}
                  </div>
                )}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="col-span-full card p-6 text-center text-slate-500 text-sm">
              학생 명단이 없거나 지도 위치를 못 찾아 보여줄 동네가 없습니다.
            </div>
          )}
        </div>
      )}

      {view === "chip" && groupBy === "school" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {schoolFiltered.map((sc) => {
            const state = schoolState(sc);
            return (
              <div
                key={sc.school}
                className={clsx(
                  "card p-3",
                  state === "mine" ? "ring-2 ring-milk-500 bg-milk-50" : state === "other" ? "bg-slate-50" : "",
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500">🎒 학교</div>
                    <div className="font-bold">{sc.school}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-extrabold text-milk-700">{sc.count}</div>
                    <div className="text-[10px] text-slate-400">가구</div>
                  </div>
                </div>
                <button
                  onClick={() => toggleSchool(sc)}
                  className={clsx(
                    "mt-2 w-full rounded-lg py-1.5 text-xs font-semibold",
                    state === "mine" ? "bg-milk-500 text-white hover:bg-milk-600" : "bg-milk-100 text-milk-700 hover:bg-milk-200",
                  )}
                >
                  {state === "mine" ? "✓ 이 학교 담당 중 — 해제" : "🎒 이 학교만 담당 (학생만)"}
                </button>
                <div className="mt-2 flex flex-wrap gap-1">
                  {sc.dongs.map((d) => {
                    const k = `${d.gu}|${d.dong}`;
                    const mine = draft.has(k);
                    const others = claimedByOthers[k] || [];
                    return (
                      <button
                        key={k}
                        onClick={() => toggle({ gu: d.gu, dong: d.dong, count: d.count })}
                        title={mine ? "담당 중 — 클릭하면 해제" : others.length ? `다른 기사: ${others.join(", ")}` : "클릭하면 담당"}
                        className={clsx(
                          "chip text-[11px] cursor-pointer",
                          mine ? "bg-milk-500 text-white"
                            : others.length ? "bg-slate-200 text-slate-600"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200",
                        )}
                      >
                        {d.gu} {d.dong} · {d.count}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {schoolFiltered.length === 0 && (
            <div className="col-span-full card p-6 text-center text-slate-500 text-sm">
              학생 명단이 없거나 지도 위치를 못 찾아 보여줄 학교가 없습니다.
            </div>
          )}
        </div>
      )}

      {view === "map" && (
        <>
          <div className="card p-2 text-xs text-slate-500 flex items-center gap-4">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.mine }} /> 내가 담당
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: STATUS_COLORS.otherClaimed }} /> 다른 기사가 맡음
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded-full border border-slate-400 bg-white" /> 미선택
            </span>
            <span className="ml-auto opacity-70">마우스 휠로 줌, 드래그로 이동</span>
          </div>
          <MapView>
            {/* 개별 학생 마커 (작게) */}
            {students
              .filter((s) => s.lat && s.lng && !s.courierFlag)
              .map((s) => (
                <Marker
                  key={s.id}
                  position={[s.lat!, s.lng!]}
                  icon={dotIcon(
                    draft.has(`${s.gu}|${s.dong}`) ? STATUS_COLORS.mine
                      : claimedByOthers[`${s.gu}|${s.dong}`]?.length ? STATUS_COLORS.otherClaimed
                      : STATUS_COLORS.pending,
                    8,
                  )}
                >
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold">{s.school} {s.name}</div>
                      <div>{s.refinedAddress}</div>
                    </div>
                  </Popup>
                </Marker>
              ))}

            {/* 동 이름 뱃지 — centroid에 */}
            {groupBy === "dong" && filtered
              .filter((c) => c.lat && c.lng)
              .map((c) => {
                const key = `${c.gu}|${c.dong}`;
                const state = draft.has(key) ? "mine" : claimedByOthers[key]?.length ? "other" : "none";
                return (
                  <Marker
                    key={key}
                    position={[c.lat!, c.lng!]}
                    icon={dongBadgeIcon(c.dong, c.count, state)}
                    eventHandlers={{ click: () => toggle(c) }}
                  >
                    <Popup>
                      <div className="text-xs">
                        <div className="font-bold">{c.gu} {c.dong} · {c.count}가구</div>
                        <div className="mt-1">
                          {state === "mine" ? (
                            <span className="text-milk-700 font-bold">✓ 담당 중 (클릭하면 해제)</span>
                          ) : state === "other" ? (
                            <span className="text-slate-500">다른 기사가 맡음 — 함께 맡을 수 있음 (누르기)</span>
                          ) : (
                            <span className="text-slate-700">클릭하면 내 담당으로 추가</span>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

            {/* 학교 뱃지 — centroid에 */}
            {groupBy === "school" && schoolFiltered
              .filter((sc) => sc.lat && sc.lng)
              .map((sc) => {
                const state = schoolState(sc);
                return (
                  <Marker
                    key={sc.school}
                    position={[sc.lat!, sc.lng!]}
                    icon={schoolBadgeIcon(sc.school, sc.count, state)}
                  >
                    <Popup>
                      <div className="text-xs min-w-[200px]">
                        <div className="font-bold">🎒 {sc.school} · {sc.count}가구</div>
                        <button
                          onClick={() => toggleSchool(sc)}
                          className={clsx(
                            "mt-1 w-full rounded py-1 text-[11px] font-semibold",
                            state === "mine" ? "bg-milk-500 text-white" : "bg-milk-100 text-milk-700",
                          )}
                        >
                          {state === "mine" ? "✓ 이 학교 담당 중 — 해제" : "🎒 이 학교만 담당"}
                        </button>
                        <div className="mt-1 text-slate-500">
                          또는 아래에서 동별로 선택할 수 있습니다.
                        </div>
                        <div className="mt-2 grid gap-1">
                          {sc.dongs.map((d) => {
                            const k = `${d.gu}|${d.dong}`;
                            const mine = draft.has(k);
                            const others = claimedByOthers[k] || [];
                            return (
                              <button
                                key={k}
                                onClick={() => toggle({ gu: d.gu, dong: d.dong, count: d.count })}
                                className={clsx(
                                  "text-left px-2 py-1 rounded text-[11px]",
                                  mine ? "bg-milk-500 text-white"
                                    : others.length ? "bg-slate-200 text-slate-600"
                                    : "bg-slate-100 hover:bg-slate-200",
                                )}
                              >
                                {d.gu} {d.dong} · {d.count}가구
                                {mine ? " ✓" : others.length ? ` (${others[0]}${others.length > 1 ? " 외" : ""})` : ""}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
          </MapView>
          {groupBy === "dong" && !studentsLoading && filtered.filter((c) => c.lat).length === 0 && (
            <div className="card p-6 text-center text-sm text-slate-500">
              지도에 뱃지가 표시되려면 먼저 학생 지도 위치가 있어야 합니다.
              <br />
              <strong>관리자 → 학생 관리 → "지도 위치 찾기"</strong> 을 먼저 실행하세요.
            </div>
          )}
          {groupBy === "school" && !studentsLoading && schoolFiltered.filter((sc) => sc.lat).length === 0 && (
            <div className="card p-6 text-center text-sm text-slate-500">
              지도에 학교 뱃지가 표시되려면 먼저 학생 지도 위치가 있어야 합니다.
              <br />
              <strong>관리자 → 학생 관리 → "지도 위치 찾기"</strong> 을 먼저 실행하세요.
            </div>
          )}
        </>
      )}
    </div>
  );
}
